<template>
  <div class="overlay-marker-container">
    <!-- This component manages OverlayView markers while maintaining
         compatibility with the existing Vue marker system -->
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { OverlayMarkerManager } from '../services/OverlayMarkerManager.js'

const props = defineProps({
  markers: {
    type: Array,
    default: () => [],
  },
  mapInstance: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['markerClick'])

let overlayManager = null

/**
 * Initialize overlay manager when map is ready
 */
const initializeOverlayManager = () => {
  if (!props.mapInstance || overlayManager) return

  overlayManager = new OverlayMarkerManager(props.mapInstance)

  // Initial sync with markers
  syncMarkersWithOverlay()
}

/**
 * Sync Vue markers array with overlay markers
 */
const syncMarkersWithOverlay = () => {
  if (!overlayManager) return

  // Clear existing overlay markers
  overlayManager.clearAllMarkers()

  // Create overlay markers from Vue markers array
  props.markers.forEach((markerData, index) => {
    const markerId = overlayManager.addMarker(markerData, {
      id: `vue-marker-${index}`,
      tooltipIndex: index, // Pass the array index for tooltip ID
      onClick: clickedMarker => {
        emit('markerClick', clickedMarker)
      },
    })

    // Store the mapping for later updates
    if (!markerData._overlayId) {
      markerData._overlayId = markerId
    }
  })
}

/**
 * Handle individual marker updates
 */
const updateMarker = (markerData, index) => {
  if (!overlayManager || !markerData._overlayId) return

  overlayManager.updateMarker(markerData._overlayId, markerData)
}

/**
 * Handle marker addition
 */
const addMarker = (markerData, index) => {
  if (!overlayManager) return

  const markerId = overlayManager.addMarker(markerData, {
    id: `vue-marker-${index}`,
    onClick: clickedMarker => {
      emit('markerClick', clickedMarker)
    },
  })

  markerData._overlayId = markerId
}

/**
 * Handle marker removal
 */
const removeMarker = markerData => {
  if (!overlayManager || !markerData._overlayId) return

  overlayManager.removeMarker(markerData._overlayId)
  delete markerData._overlayId
}

/**
 * Watch for map instance changes
 */
watch(
  () => props.mapInstance,
  newMapInstance => {
    if (newMapInstance) {
      // Clean up existing manager
      if (overlayManager) {
        overlayManager.destroy()
        overlayManager = null
      }

      // Initialize with new map instance
      nextTick(() => {
        initializeOverlayManager()
      })
    }
  },
  { immediate: true },
)

/**
 * Watch for markers array changes
 */
watch(
  () => props.markers,
  (newMarkers, oldMarkers) => {
    if (!overlayManager) return

    // Handle bulk changes - just resync for simplicity
    syncMarkersWithOverlay()
  },
  { deep: true },
)

/**
 * Watch for individual marker changes for granular updates
 */
watch(
  () => props.markers.map(m => m.position),
  (newPositions, oldPositions) => {
    if (!overlayManager) return

    // Update positions of existing markers
    props.markers.forEach((markerData, index) => {
      if (markerData._overlayId) {
        overlayManager.updateMarkerPosition(
          markerData._overlayId,
          markerData.position,
        )
      }
    })
  },
  { deep: true },
)

/**
 * Lifecycle hooks
 */
onMounted(() => {
  initializeOverlayManager()
})

onBeforeUnmount(() => {
  if (overlayManager) {
    overlayManager.destroy()
    overlayManager = null
  }
})

/**
 * Expose methods for external use
 */
defineExpose({
  // Get overlay manager instance
  getOverlayManager: () => overlayManager,

  // Force redraw all markers
  redrawMarkers: () => {
    if (overlayManager) {
      overlayManager.redrawAllMarkers()
    }
  },

  // Clear all markers
  clearMarkers: () => {
    if (overlayManager) {
      overlayManager.clearAllMarkers()
    }
  },

  // Get marker count
  getMarkerCount: () => {
    return overlayManager ? overlayManager.getMarkerCount() : 0
  },
})
</script>

<style scoped>
.overlay-marker-container {
  /* This container doesn't render any visible content */
  /* It only manages the OverlayView markers */
}
</style>
